/**
 * Button Component
 * ================
 * Single reusable button for the entire app.
 * NEVER create a one-off styled button in a page component.
 *
 * Variants:  primary | secondary | ghost | danger
 * Sizes:     sm | md | lg
 * States:    loading, disabled — handled internally
 *
 * Accessibility:
 *  - Correct role="button" (default) or role="link" when asChild
 *  - aria-disabled prevents interaction when disabled
 *  - aria-busy signals loading state to screen readers
 *  - Focus ring via :focus-visible in global CSS
 */

import React from "react";
import { cn } from "@/theme/utils";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success";
type ButtonSize    = "sm" | "md" | "lg";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables interaction */
  loading?: boolean;
  /** Renders an icon to the left of children */
  leftIcon?: React.ReactNode;
  /** Renders an icon to the right of children */
  rightIcon?: React.ReactNode;
  /** Makes the button take full container width */
  fullWidth?: boolean;
}

// ─── STYLE MAPS ──────────────────────────────────────────────────────────────
// All values reference Tailwind classes derived from tokens.
// If you need a new variant — add it here. Do NOT add inline styles.

const variantStyles: Record<ButtonVariant, string> = {
  primary: [
    "bg-primary text-white border-transparent",
    "hover:bg-primary-700 hover:-translate-y-px",
    "active:translate-y-0",
    "shadow-btn hover:shadow-lg",
    "disabled:bg-primary/50 disabled:shadow-none disabled:translate-y-0",
  ].join(" "),

  secondary: [
    "bg-white text-primary border-border-mid",
    "hover:border-primary hover:bg-surface-alt",
    "disabled:text-text-muted disabled:border-border",
  ].join(" "),

  ghost: [
    "bg-transparent text-primary border-transparent",
    "hover:bg-surface-alt",
    "disabled:text-text-muted",
  ].join(" "),

  danger: [
    "bg-error text-white border-transparent",
    "hover:bg-red-700 hover:-translate-y-px",
    "active:translate-y-0",
    "disabled:bg-error/50",
  ].join(" "),

  success: [
    "bg-accent text-white border-transparent",
    "hover:bg-teal-700 hover:-translate-y-px",
    "active:translate-y-0",
    "disabled:bg-accent/50",
  ].join(" "),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "h-8  px-3  text-xs  gap-1.5 rounded-md",
  md: "h-10 px-4  text-sm  gap-2   rounded-lg",
  lg: "h-12 px-6  text-sm  gap-2.5 rounded-xl",
};

// ─── SPINNER ─────────────────────────────────────────────────────────────────
function Spinner({ size }: { size: ButtonSize }) {
  const dim = size === "sm" ? "w-3 h-3" : size === "lg" ? "w-5 h-5" : "w-4 h-4";
  return (
    <span
      aria-hidden="true"
      className={cn(
        dim,
        "border-2 border-current border-t-transparent rounded-full animate-spin"
      )}
    />
  );
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size    = "md",
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled ?? loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        className={cn(
          // Base
          "inline-flex items-center justify-center font-body font-bold",
          "border transition cursor-pointer",
          "select-none whitespace-nowrap",
          // Variant + size
          variantStyles[variant],
          sizeStyles[size],
          // Full width
          fullWidth && "w-full",
          // Disabled
          "disabled:cursor-not-allowed disabled:pointer-events-none",
          // Custom override
          className
        )}
        {...props}
      >
        {loading
          ? <Spinner size={size} />
          : leftIcon && <span aria-hidden="true">{leftIcon}</span>
        }
        {children}
        {!loading && rightIcon && (
          <span aria-hidden="true">{rightIcon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
